if (!customElements.get('lifestyle-banner-component')) {
  class LifestyleBannerComponent extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.isPlaying = false;
      this.autoplayTimer = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    }

    connectedCallback() {
      this.slider = this.querySelector('[data-lifestyle-slider]');
      this.slides = Array.from(this.querySelectorAll('[data-lifestyle-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-lifestyle-dot]'));
      this.prevButton = this.querySelector('[data-lifestyle-prev]');
      this.nextButton = this.querySelector('[data-lifestyle-next]');
      this.autoplayButton = this.querySelector('[data-lifestyle-autoplay]');
      this.currentPageElement = this.querySelector('[data-lifestyle-current]');
      this.totalPagesElement = this.querySelector('[data-lifestyle-total]');
      this.root = this.closest('.lifestyle-banner') || this;

      if (!this.slider || this.slides.length === 0) return;

      this.autoplayEnabled = this.slider.dataset.autoplay === 'true';
      this.pauseOnHover = this.slider.dataset.pauseOnHover === 'true';
      this.autoplaySpeed = Number(this.slider.dataset.speed || 5) * 1000;

      if (this.totalPagesElement) {
        this.totalPagesElement.textContent = String(this.slides.length);
      }

      this.prevButton?.addEventListener('click', () => this.goTo(this.currentIndex - 1));
      this.nextButton?.addEventListener('click', () => this.goTo(this.currentIndex + 1));
      this.querySelector('.lifestyle-banner__controls')?.addEventListener('click', (event) => {
        const dot = event.target.closest('[data-lifestyle-dot]');
        if (!dot || !this.dots.includes(dot)) return;
        event.preventDefault();
        this.goTo(this.dots.indexOf(dot));
      });
      this.autoplayButton?.addEventListener('click', () => this.toggleAutoplay());

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', this.handleMouseEnter);
        this.addEventListener('mouseleave', this.handleMouseLeave);
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
      }

      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) {
          this.pause(true);
        } else if (this.autoplayEnabled && this.isPlaying) {
          this.play();
        }
      });

      this.onBlockSelect = (event) => {
        const slide = event.target.closest('[data-lifestyle-slide]');
        if (!slide || !this.contains(slide)) return;
        const index = this.slides.indexOf(slide);
        if (index < 0) return;
        this.pause(true);
        this.goTo(index);
      };

      this.onBlockDeselect = (event) => {
        const slide = event.target.closest('[data-lifestyle-slide]');
        if (!slide || !this.contains(slide)) return;
        if (this.autoplayEnabled) {
          this.play();
          this.updateAutoplayButton(false);
        }
      };

      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.onBlockDeselect);

      this.updateAvailableHeight = this.updateAvailableHeight.bind(this);
      this.updateAvailableHeight();
      window.addEventListener('resize', this.updateAvailableHeight);
      // Header height var is set after sticky-header connects
      requestAnimationFrame(() => this.updateAvailableHeight());

      this.goTo(0, false);

      if (this.autoplayEnabled && this.slides.length > 1 && !this.reducedMotion.matches) {
        this.play();
        this.updateAutoplayButton(false);
      }
    }

    disconnectedCallback() {
      this.pause(true);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
      window.removeEventListener('resize', this.updateAvailableHeight);

      if (this.pauseOnHover) {
        this.removeEventListener('mouseenter', this.handleMouseEnter);
        this.removeEventListener('mouseleave', this.handleMouseLeave);
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
      }
    }

    updateAvailableHeight() {
      if (!this.slider?.classList.contains('lifestyle-banner--fullscreen')) return;

      const offsetTop = this.root.getBoundingClientRect().top + window.scrollY;
      const available = Math.max(Math.round(window.innerHeight - offsetTop), 280);
      this.root.style.setProperty('--lifestyle-available-height', `${available}px`);
    }

    handleMouseEnter = () => {
      this.pause();
    };

    handleMouseLeave = () => {
      if (this.autoplayEnabled && this.isPlaying) this.play();
    };

    handleFocusIn = () => {
      this.pause();
    };

    handleFocusOut = (event) => {
      if (!this.contains(event.relatedTarget) && this.autoplayEnabled && this.isPlaying) {
        this.play();
      }
    };

    goTo(index, animate = true) {
      if (this.slides.length === 0) return;

      const total = this.slides.length;
      const nextIndex = ((index % total) + total) % total;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === nextIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');

        if (isActive) {
          slide.removeAttribute('tabindex');
        } else {
          slide.setAttribute('tabindex', '-1');
        }

        if (!animate) {
          slide.classList.add('no-transition');
          requestAnimationFrame(() => slide.classList.remove('no-transition'));
        }

        slide.querySelectorAll('a, button').forEach((el) => {
          if (isActive) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });

        const video = slide.querySelector('video');
        if (video) {
          if (isActive) {
            video.play?.().catch(() => {});
          } else {
            video.pause?.();
          }
        }
      });

      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === nextIndex;
        dot.classList.toggle('slider-counter__link--active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });

      if (this.currentPageElement) {
        this.currentPageElement.textContent = String(nextIndex + 1);
      }

      this.currentIndex = nextIndex;
    }

    play() {
      this.pause();
      if (this.slides.length < 2 || this.reducedMotion.matches) return;
      this.isPlaying = true;
      this.autoplayTimer = setInterval(() => this.goTo(this.currentIndex + 1), this.autoplaySpeed);
    }

    pause(clearPlayingState = false) {
      if (this.autoplayTimer) {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = null;
      }
      if (clearPlayingState) this.isPlaying = false;
    }

    toggleAutoplay() {
      if (this.isPlaying) {
        this.pause(true);
        this.updateAutoplayButton(true);
      } else {
        this.play();
        this.updateAutoplayButton(false);
      }
    }

    updateAutoplayButton(paused) {
      if (!this.autoplayButton) return;
      this.autoplayButton.classList.toggle('slideshow__autoplay--paused', paused);
      const label = paused
        ? window.accessibilityStrings?.playSlideshow
        : window.accessibilityStrings?.pauseSlideshow;
      if (label) this.autoplayButton.setAttribute('aria-label', label);
    }
  }

  customElements.define('lifestyle-banner-component', LifestyleBannerComponent);
}
